import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Building2, Store, Users, CreditCard, Image,
  Check, ChevronRight, ChevronLeft, X, Upload,
  Loader2, Plus, Trash2, Rocket
} from 'lucide-react';

const STEPS = [
  { key: 'company', label: 'Entreprise', icon: Building2, desc: 'Informations de votre société' },
  { key: 'shop', label: 'Boutique', icon: Store, desc: 'Votre première boutique' },
  { key: 'team', label: 'Équipe', icon: Users, desc: 'Utilisateurs et rôles' },
  { key: 'billing', label: 'Facturation', icon: CreditCard, desc: 'Paramètres de facturation' },
  { key: 'branding', label: 'Logo & Visibilité', icon: Image, desc: 'Logo et paramètres' },
];

const GY_GREEN = '#009b3a';
const GY_YELLOW = '#fedd00';

const OnboardingWizard = ({ onComplete }) => {
  const { api } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Step 1: Company info
  const [company, setCompany] = useState({ name: '', email: '', phone: '', address: '', city: '', siret: '', description: '' });
  // Step 2: Shop
  const [shop, setShop] = useState({ shop_name: '', shop_address: '', shop_city: '', shop_phone: '', shop_email: '', shop_hours: '' });
  // Step 3: Team
  const [members, setMembers] = useState([]);
  // Step 4: Billing
  const [billing, setBilling] = useState({ billing_email: '', billing_address: '', vat_number: '' });
  // Step 5: Branding
  const [branding, setBranding] = useState({ visible_in_directory: true, description: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/onboarding/status');
      const c = res.data.company || {};
      setCompany({
        name: c.name || '', email: c.email || '', phone: c.phone || '',
        address: c.address || '', city: c.city || '', siret: c.siret || '',
        description: c.description || ''
      });
      setShop(s => ({ ...s, shop_city: c.city || '', shop_email: c.email || '', shop_phone: c.phone || '' }));
      setBilling(b => ({ ...b, billing_email: c.email || '' }));
      setBranding({ visible_in_directory: c.visible_in_directory !== false, description: c.description || '' });
      if (c.logo_url) setLogoPreview(c.logo_url);
      setStep(Math.min(res.data.current_step || 0, STEPS.length - 1));
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const saveStep = async () => {
    setError('');
    setSaving(true);
    try {
      if (step === 0) {
        if (!company.name.trim()) { setError("Le nom de l'entreprise est requis"); setSaving(false); return; }
        await api.post('/onboarding/step/company-info', company);
      } else if (step === 1) {
        await api.post('/onboarding/step/first-shop', shop);
      } else if (step === 2) {
        const validMembers = members.filter(m => m.username && m.password);
        await api.post('/onboarding/step/team', { members: validMembers });
      } else if (step === 3) {
        await api.post('/onboarding/step/billing', billing);
      } else if (step === 4) {
        if (logoFile) {
          const fd = new FormData();
          fd.append('file', logoFile);
          await api.post('/onboarding/upload-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        await api.post('/onboarding/step/branding', branding);
      }
      if (step < STEPS.length - 1) {
        setStep(step + 1);
      } else {
        await api.post('/onboarding/complete');
        onComplete();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const skipWizard = async () => {
    try {
      await api.post('/onboarding/skip');
      onComplete();
    } catch {
      onComplete();
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#050a14] z-[100] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#009b3a]" />
      </div>
    );
  }

  const addMember = () => setMembers([...members, { username: '', password: '', role: 'user', email: '', telephone: '' }]);
  const removeMember = (i) => setMembers(members.filter((_, idx) => idx !== i));
  const updateMember = (i, field, val) => {
    const copy = [...members];
    copy[i] = { ...copy[i], [field]: val };
    setMembers(copy);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const isLastStep = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 bg-[#050a14] z-[100] flex flex-col" data-testid="onboarding-wizard">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
        <div className="flex items-center gap-3">
          <Rocket className="w-5 h-5" style={{ color: GY_GREEN }} />
          <h1 className="text-base font-bold text-white">Configuration de votre espace</h1>
        </div>
        <button onClick={skipWizard} data-testid="onboarding-skip-btn" className="text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1">
          <X className="w-3.5 h-3.5" /> Passer
        </button>
      </div>

      {/* Progress bar */}
      <div className="px-6 py-4">
        <div className="flex items-center gap-1 mb-2">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex-1 flex items-center gap-1">
              <div className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? 'bg-[#009b3a]' : 'bg-white/10'}`} />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {React.createElement(STEPS[step].icon, { className: 'w-4 h-4', style: { color: GY_GREEN } })}
          <span className="text-sm font-semibold text-white">{STEPS[step].label}</span>
          <span className="text-xs text-gray-500">— {STEPS[step].desc}</span>
          <span className="ml-auto text-xs text-gray-600">{step + 1}/{STEPS.length}</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="max-w-xl mx-auto">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400" data-testid="onboarding-error">{error}</div>
          )}

          {/* Step 1: Company Info */}
          {step === 0 && (
            <div className="space-y-4" data-testid="onboarding-step-company">
              <Field label="Nom de l'entreprise *" value={company.name} onChange={v => setCompany({ ...company, name: v })} testId="onb-company-name" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email" value={company.email} onChange={v => setCompany({ ...company, email: v })} type="email" testId="onb-company-email" />
                <Field label="Téléphone" value={company.phone} onChange={v => setCompany({ ...company, phone: v })} testId="onb-company-phone" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ville" value={company.city} onChange={v => setCompany({ ...company, city: v })} testId="onb-company-city" />
                <Field label="SIRET" value={company.siret} onChange={v => setCompany({ ...company, siret: v })} testId="onb-company-siret" />
              </div>
              <Field label="Adresse" value={company.address} onChange={v => setCompany({ ...company, address: v })} testId="onb-company-address" />
              <Field label="Description courte" value={company.description} onChange={v => setCompany({ ...company, description: v })} textarea testId="onb-company-desc" />
            </div>
          )}

          {/* Step 2: First Shop */}
          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-shop">
              <Field label="Nom de la boutique" value={shop.shop_name} onChange={v => setShop({ ...shop, shop_name: v })} placeholder="Boutique principale" testId="onb-shop-name" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Adresse" value={shop.shop_address} onChange={v => setShop({ ...shop, shop_address: v })} testId="onb-shop-address" />
                <Field label="Ville" value={shop.shop_city} onChange={v => setShop({ ...shop, shop_city: v })} testId="onb-shop-city" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Téléphone" value={shop.shop_phone} onChange={v => setShop({ ...shop, shop_phone: v })} testId="onb-shop-phone" />
                <Field label="Email" value={shop.shop_email} onChange={v => setShop({ ...shop, shop_email: v })} testId="onb-shop-email" />
              </div>
              <Field label="Horaires d'ouverture" value={shop.shop_hours} onChange={v => setShop({ ...shop, shop_hours: v })} placeholder="Lun-Ven 9h-18h, Sam 10h-16h" testId="onb-shop-hours" />
            </div>
          )}

          {/* Step 3: Team */}
          {step === 2 && (
            <div className="space-y-4" data-testid="onboarding-step-team">
              <p className="text-xs text-gray-400">Ajoutez vos techniciens et collaborateurs. Vous pourrez toujours en ajouter plus tard.</p>
              {members.map((m, i) => (
                <div key={i} className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400 font-semibold">Membre {i + 1}</span>
                    <button onClick={() => removeMember(i)} className="p-1 rounded hover:bg-red-500/10" data-testid={`onb-remove-member-${i}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={m.username} onChange={e => updateMember(i, 'username', e.target.value)} placeholder="Nom d'utilisateur" className="px-3 py-2 bg-[#0a0f1c] border border-white/10 rounded-lg text-xs text-white" data-testid={`onb-member-username-${i}`} />
                    <input value={m.password} onChange={e => updateMember(i, 'password', e.target.value)} placeholder="Mot de passe" type="password" className="px-3 py-2 bg-[#0a0f1c] border border-white/10 rounded-lg text-xs text-white" data-testid={`onb-member-password-${i}`} />
                  </div>
                  <select value={m.role} onChange={e => updateMember(i, 'role', e.target.value)} className="w-full px-3 py-2 bg-[#0a0f1c] border border-white/10 rounded-lg text-xs text-white" data-testid={`onb-member-role-${i}`}>
                    <option value="user">Employé</option>
                    <option value="technicien">Technicien</option>
                  </select>
                </div>
              ))}
              <button onClick={addMember} data-testid="onb-add-member" className="w-full py-2.5 rounded-lg border border-dashed border-white/10 text-xs text-gray-400 hover:border-white/20 hover:text-white flex items-center justify-center gap-2 transition-all">
                <Plus className="w-3.5 h-3.5" /> Ajouter un membre
              </button>
              {members.length === 0 && (
                <p className="text-xs text-gray-600 text-center">Aucun membre ajouté. Vous pourrez le faire plus tard depuis les paramètres.</p>
              )}
            </div>
          )}

          {/* Step 4: Billing */}
          {step === 3 && (
            <div className="space-y-4" data-testid="onboarding-step-billing">
              <p className="text-xs text-gray-400">Ces informations apparaîtront sur vos factures.</p>
              <Field label="Email de facturation" value={billing.billing_email} onChange={v => setBilling({ ...billing, billing_email: v })} type="email" testId="onb-billing-email" />
              <Field label="Adresse de facturation" value={billing.billing_address} onChange={v => setBilling({ ...billing, billing_address: v })} textarea testId="onb-billing-address" />
              <Field label="N° TVA (optionnel)" value={billing.vat_number} onChange={v => setBilling({ ...billing, vat_number: v })} placeholder="FR12345678901" testId="onb-billing-vat" />
            </div>
          )}

          {/* Step 5: Branding */}
          {step === 4 && (
            <div className="space-y-4" data-testid="onboarding-step-branding">
              <div>
                <label className="block text-xs text-gray-400 mb-2">Logo de l'entreprise</label>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <img src={logoPreview.startsWith('blob:') ? logoPreview : `${process.env.REACT_APP_BACKEND_URL}${logoPreview}`} alt="Logo" className="w-16 h-16 rounded-lg object-cover border border-white/10" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-white/5 border border-dashed border-white/10 flex items-center justify-center">
                      <Image className="w-6 h-6 text-gray-600" />
                    </div>
                  )}
                  <label className="cursor-pointer px-4 py-2 rounded-lg border border-white/10 text-xs text-gray-300 hover:bg-white/5 flex items-center gap-2 transition-all" data-testid="onb-upload-logo-btn">
                    <Upload className="w-3.5 h-3.5" /> Choisir un fichier
                    <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={handleLogoChange} className="hidden" />
                  </label>
                </div>
                <p className="text-[10px] text-gray-600 mt-1">PNG, JPG, WEBP ou SVG. Max 5 Mo.</p>
              </div>
              <Field label="Description (annuaire)" value={branding.description} onChange={v => setBranding({ ...branding, description: v })} textarea testId="onb-branding-desc" />
              <label className="flex items-center gap-2 cursor-pointer" data-testid="onb-directory-toggle">
                <input type="checkbox" checked={branding.visible_in_directory} onChange={e => setBranding({ ...branding, visible_in_directory: e.target.checked })} className="rounded border-gray-600 text-[#009b3a]" />
                <span className="text-xs text-gray-400">Apparaître dans l'annuaire public GUYAREPAIR</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-white/5 flex items-center justify-between">
        <button
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          data-testid="onboarding-prev-btn"
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-all"
        >
          <ChevronLeft className="w-4 h-4" /> Précédent
        </button>
        <button
          onClick={saveStep}
          disabled={saving}
          data-testid="onboarding-next-btn"
          className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm text-[#050a14] disabled:opacity-50 transition-all hover:opacity-90"
          style={{ background: `linear-gradient(135deg, ${GY_GREEN}, ${GY_YELLOW})` }}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {isLastStep ? 'Terminer' : 'Suivant'} {!isLastStep && <ChevronRight className="w-4 h-4" />}
          {isLastStep && <Check className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

/* ─── Reusable Field Component ─── */
const Field = ({ label, value, onChange, type = 'text', placeholder, textarea, testId }) => (
  <div>
    <label className="block text-xs text-gray-400 mb-1">{label}</label>
    {textarea ? (
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        data-testid={testId}
        className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40 resize-none"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40"
      />
    )}
  </div>
);

export default OnboardingWizard;
